import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { ShortcutHint } from '@/src/renderer/components/ui/ShortcutHint'
import { CheckCircleFilledIcon, TaskDeadlineIcon } from '@/src/renderer/components/icons'
import { MiniCalendar } from '../WhenDropdown/MiniCalendar'
import { PeriodOptions } from '../WhenDropdown/PeriodOptions'
import { TimePointRow } from '../WhenDropdown/TimePointRow'
import { WhenDropdownFooter } from '../WhenDropdown/WhenDropdownFooter'
import { TodayIcon, TomorrowIcon, PeriodIcon, ClockIcon } from '../WhenDropdown/WhenDropdownIcons'
import { isToday, isTomorrow } from '../WhenDropdown/whenDropdownUtils'
import { formatDeadlineDate } from './deadlineDropdownUtils'
import { useDateTimeDropdownState } from '../../hooks/useDateTimeDropdownState'
import type { DeadlineDropdownContentProps } from './DeadlineDropdown.types'
import type { StartValue } from '../WhenDropdown/WhenDropdown.types'
import './DeadlineDropdown.scss'

/**
 * DeadlineDropdownContent — Dropdown for selecting task deadline date/time.
 *
 * Two-column layout (mirrors WhenDropdown):
 * - Left: Day selection (Today/Tomorrow quick picks + calendar)
 * - Right: Time period or exact time selection
 *
 * Saves only on "Confirm" button click — except in `inline` (Aufgaben-Details),
 * where every change is written at once (ADR 0026; same hook as WhenDropdown).
 */
export const DeadlineDropdownContent: React.FC<DeadlineDropdownContentProps> = ({
  value,
  onValueChange,
  onClose,
  onRemove,
  placement = 'actionbar',
}) => {
  const { t } = useTranslation()
  const inline = placement === 'inline'

  const state = useDateTimeDropdownState({
    value,
    onChange: onValueChange,
    onClose,
    formatDisplay: formatDeadlineDate,
    autoFocus: !inline,
    inline,
  })

  // Adapter: DeadlineValue → StartValue for PeriodOptions
  const handlePeriodOnChange = useCallback((sv: StartValue) => {
    onValueChange({ date: sv.date, time: sv.time ?? null, period: sv.period })
  }, [onValueChange])

  // ── Display icon for NL input ──
  let displayIcon: React.ReactNode = <TaskDeadlineIcon size={16} />
  if (state.timeSource === 'time' && state.selectedTime) displayIcon = <ClockIcon size={16} />
  else if (state.timeSource === 'period' && state.selectedPeriod) displayIcon = <PeriodIcon period={state.selectedPeriod} size={16} />
  else if (state.daySource === 'quick' && state.selectedDate) {
    if (isToday(state.selectedDate)) displayIcon = <TodayIcon size={16} />
    else if (isTomorrow(state.selectedDate)) displayIcon = <TomorrowIcon size={16} />
  }

  return (
    <div className="deadline-dropdown" data-placement={placement} onKeyDown={state.handleGlobalKeyDown}>
      {/* NL Input */}
      <div className="deadline-dropdown__search-wrapper">
        <div className="deadline-dropdown__search">
          {displayIcon}
          <input
            ref={state.inputRef}
            className="deadline-dropdown__input"
            placeholder={t('taskCreation.actionDeadline')}
            value={state.inputValue ?? state.displayText}
            onChange={(e) => state.setInputValue(e.target.value)}
            onKeyDown={state.handleInputKeyDown}
            onFocus={() => { if (state.inputValue === null && state.displayText) state.setInputValue(state.displayText) }}
            // In den Aufgaben-Details stehen sieben Inhalte gleichzeitig offen — sechs
            // von ihnen hätten ein `autoFocus`, und der letzte gewänne. Der Fokus gehört
            // dort der Liste (CONTEXT.md, Aufgaben-Details), bis jemand hineingreift.
            autoFocus={!inline}
          />
        </div>
      </div>

      {/* Two-column selection */}
      <div className="deadline-dropdown__selection">
        <div className="deadline-dropdown__columns">
          {/* Day Column */}
          <div className="deadline-dropdown__column deadline-dropdown__column--day">
            <div className="deadline-dropdown__column-label">
              {t('taskCreation.whenDayLabel')}
            </div>

            {/* Today */}
            <button
              type="button"
              className={`deadline-dropdown__item${state.daySource === 'quick' && state.selectedDate && isToday(state.selectedDate) ? ' deadline-dropdown__item--selected' : ''}`}
              onClick={() => state.handleQuickDay(0)}
              // In `inline` hat der erste Klick schon geschrieben — siehe WhenDropdown.
              onDoubleClick={inline ? undefined : () => {
                const d = new Date(); d.setHours(0, 0, 0, 0)
                onValueChange({ date: d, time: null, period: null })
                onClose()
              }}
            >
              <TodayIcon size={16} />
              <span className="deadline-dropdown__item-text">{t('taskCreation.whenToday')}</span>
              <div className="deadline-dropdown__item-right">
                {state.daySource === 'quick' && state.selectedDate && isToday(state.selectedDate) && (
                  <CheckCircleFilledIcon size={16} className="deadline-dropdown__check" />
                )}
                <ShortcutHint>1</ShortcutHint>
              </div>
            </button>

            {/* Tomorrow */}
            <button
              type="button"
              className={`deadline-dropdown__item${state.daySource === 'quick' && state.selectedDate && isTomorrow(state.selectedDate) ? ' deadline-dropdown__item--selected' : ''}`}
              onClick={() => state.handleQuickDay(1)}
              onDoubleClick={inline ? undefined : () => {
                const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(0, 0, 0, 0)
                onValueChange({ date: d, time: null, period: null })
                onClose()
              }}
            >
              <TomorrowIcon size={16} />
              <span className="deadline-dropdown__item-text">{t('taskCreation.whenTomorrow')}</span>
              <div className="deadline-dropdown__item-right">
                {state.daySource === 'quick' && state.selectedDate && isTomorrow(state.selectedDate) && (
                  <CheckCircleFilledIcon size={16} className="deadline-dropdown__check" />
                )}
                <ShortcutHint>2</ShortcutHint>
              </div>
            </button>

            {/* Mini Calendar */}
            <MiniCalendar
              selectedDate={state.selectedDate}
              activeSelection={state.daySource === 'calendar'}
              overdueVariant="deadline"
              onSelect={state.handleCalendarDay}
              onDoubleClick={inline ? undefined : (date) => {
                state.handleCalendarDay(date)
                onValueChange({ date, time: state.selectedTime || null, period: state.selectedPeriod })
                onClose()
              }}
            />
          </div>

          {/* Vertical separator */}
          <div className="deadline-dropdown__separator-vertical" />

          {/* Time Column */}
          <div className="deadline-dropdown__column deadline-dropdown__column--time">
            <div className="deadline-dropdown__column-label">
              {t('taskCreation.whenTimeLabel')}
            </div>

            <PeriodOptions
              selectedDate={state.selectedDate}
              selectedPeriod={state.selectedPeriod}
              selectedTime={state.selectedTime}
              timeSource={state.timeSource}
              onPeriodSelect={state.handlePeriodSelect}
              onValueChange={handlePeriodOnChange}
              onClose={onClose}
            />

            <TimePointRow
              selectedTime={state.selectedTime}
              timeSource={state.timeSource}
              hasSelection={state.hasSelection}
              onTimeChange={state.setSelectedTime}
              onApplyTime={state.applyTimeValue}
              onClearTime={state.handleTimeClear}
              onConfirm={state.handleConfirm}
              onClose={onClose}
              inline={inline}
            />
          </div>
        </div>
      </div>

      <WhenDropdownFooter
        placement={placement}
        hasSelection={state.hasSelection}
        hasExistingDate={value != null}
        onConfirm={state.handleConfirm}
        onRemove={onRemove}
        onClose={onClose}
      />
    </div>
  )
}
